/*
 * One `/api/events` stream per tab, shared by whatever is mounted: the case
 * chat follows its thread, the agent panel follows its scope, the
 * notifications bell follows the user. Scopes are keyed by owner so several
 * views can be live at once. Mounted once inside the authenticated shell.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import axiosInstance, { getAuthToken } from "../../api/axiosConfig";
import {
  EventStreamClient,
  type RealtimeEvent,
  type StreamScope,
  type StreamStatus
} from "./eventStream";

type RealtimeContextValue = {
  status: StreamStatus;
  /** Listen for events of one type (or `"*"`). Returns an unsubscribe. */
  subscribe: (
    type: string,
    handler: (event: RealtimeEvent) => void
  ) => () => void;
  /** Claim (or with `null`, release) the scope followed on behalf of `owner`. */
  setScope: (owner: string, scope: StreamScope | null) => void;
};

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export const eventsUrl = (): string => {
  const root = (axiosInstance.defaults.baseURL ?? "").replace(/\/+$/, "");
  return `${root}/events`;
};

type Props = {
  children: ReactNode;
  /** Test seam. */
  client?: EventStreamClient;
  /** Off in tests / when the backend does not stream. */
  enabled?: boolean;
};

export const RealtimeProvider: FC<Props> = ({
  children,
  client: injected,
  enabled = true
}) => {
  const client = useMemo(
    () =>
      injected ??
      new EventStreamClient({ url: eventsUrl(), getToken: getAuthToken }),
    [injected]
  );
  const [status, setStatus] = useState<StreamStatus>(client.status);
  const handlers = useRef(new Map<string, Set<(e: RealtimeEvent) => void>>());

  useEffect(() => {
    const offStatus = client.onStatus(setStatus);
    const offEvents = client.subscribe((event) => {
      handlers.current.get(event.type)?.forEach((h) => h(event));
      handlers.current.get("*")?.forEach((h) => h(event));
    });
    if (enabled) client.start();
    return () => {
      offStatus();
      offEvents();
      client.stop();
    };
  }, [client, enabled]);

  // A hidden tab holds no connection; it resumes from its cursor when shown.
  useEffect(() => {
    if (!enabled) return;
    const sync = () => {
      if (document.visibilityState === "hidden") client.pause();
      else client.resume();
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, [client, enabled]);

  const subscribe = useCallback(
    (type: string, handler: (event: RealtimeEvent) => void) => {
      let set = handlers.current.get(type);
      if (!set) {
        set = new Set();
        handlers.current.set(type, set);
      }
      set.add(handler);
      return () => {
        set?.delete(handler);
      };
    },
    []
  );
  const setScope = useCallback(
    (owner: string, scope: StreamScope | null) => client.setScope(owner, scope),
    [client]
  );

  const value = useMemo(
    () => ({ status, subscribe, setScope }),
    [status, subscribe, setScope]
  );
  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

const NOOP: RealtimeContextValue = {
  status: "idle",
  subscribe: () => () => {},
  setScope: () => {}
};

/** Null-safe: outside the provider (unit tests, public pages) it is inert. */
export const useRealtime = (): RealtimeContextValue =>
  useContext(RealtimeContext) ?? NOOP;

/** Run `handler` for every event of `type` while mounted. */
export const useRealtimeEvent = <T = unknown,>(
  type: string,
  handler: (data: T, event: RealtimeEvent) => void
): void => {
  const { subscribe } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(
    () => subscribe(type, (e) => ref.current(e.data as T, e)),
    [subscribe, type]
  );
};

/**
 * Follow a scope/thread while mounted on behalf of `owner` (one per view, e.g.
 * `"case-ide"` or `"agent-panel"`); releases it on unmount.
 */
export const useRealtimeScope = (
  owner: string,
  scope: StreamScope | null
): void => {
  const { setScope } = useRealtime();
  const kind = scope?.kind ?? "case";
  const scopeId = scope?.scopeId ?? scope?.individualId ?? null;
  const threadId = scope?.threadId ?? null;
  const afterNs = scope?.afterNs ?? null;
  const afterNsRef = useRef(afterNs);
  afterNsRef.current = afterNs;
  useEffect(() => {
    setScope(
      owner,
      scopeId ? { kind, scopeId, threadId, afterNs: afterNsRef.current } : null
    );
    // `afterNs` is only a resume hint; it must not force a reconnect.
  }, [setScope, owner, kind, scopeId, threadId]);
  useEffect(() => () => setScope(owner, null), [setScope, owner]);
};
